import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import HelperNote from '../primitives/HelperNote';
import PillGroup from '../primitives/PillGroup';
import SelectField from '../primitives/SelectField';
import TextField from '../primitives/TextField';
import CheckboxGrid from '../primitives/CheckboxGrid';
import Tooltip from '../primitives/Tooltip';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';

export const WHY_OPTIONS = [
  { key: 'wide_choice', label: 'Wide choice of lenders' },
  { key: 'expert_advice', label: 'Expert advice' },
  { key: 'convienence', label: 'Convenience' },
  { key: 'legwork', label: 'Let us do the legwork' },
  { key: 'existing_customer', label: 'Existing customer' },
];

export const ASSIST_OPTIONS = [
  { key: 'purchase', label: 'Purchase a property' },
  { key: 'refinance', label: 'Refinance existing loan' },
  { key: 'build_new_home', label: 'Build a new home' },
  { key: 'existing_loan', label: 'Manage an existing loan' },
];

export const PAYMENT_OPTIONS = [
  { key: 'repayment_prior_to_retirement', label: 'Repay loan before retirement' },
  { key: 'downsizing', label: 'Downsize property' },
  { key: 'sale_of_assets', label: 'Sale of assets' },
  { key: 'super_income', label: 'Super income stream' },
  { key: 'super_lump_sum', label: 'Super lump sum' },
  { key: 'savings', label: 'Savings' },
  { key: 'other_investments', label: 'Other investments' },
  { key: 'co_app_income', label: 'Co-applicant income' },
  { key: 'other_check', label: 'Other (specify below)' },
];

const TERM_OPTIONS = [
  { value: '5', label: '5 years' },
  { value: '10', label: '10 years' },
  { value: '15', label: '15 years' },
  { value: '20', label: '20 years' },
  { value: '25', label: '25 years' },
  { value: '30', label: '30 years' },
  { value: 'other', label: 'Other' },
];

const INTEREST_ONLY_TERMS = [
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: 'other', label: 'Other' },
];

const REPAYMENT_OPTIONS = [
  {
    value: 'Principal & Interest',
    label: 'Principal & Interest',
    tip: 'You pay down the loan balance plus interest each repayment. Standard for owner-occupier loans.',
  },
  {
    value: 'Interest Only',
    label: 'Interest Only',
    tip: 'You only pay the interest for a set period (commonly 1-5 years). Repayments are lower, but the loan balance doesn\'t reduce during this time.',
  },
];

const RATE_OPTIONS = [
  {
    value: 'Fixed',
    label: 'Fixed',
    tip: 'Your interest rate is locked in for a set period (typically 1-5 years). Predictable repayments, but limited flexibility.',
  },
  {
    value: 'Variable',
    label: 'Variable',
    tip: 'The interest rate can go up or down based on the lender / market. More features and flexibility, but repayments can change.',
  },
  {
    value: 'Split',
    label: 'Split',
    tip: 'A portion of the loan is fixed and a portion is variable. Gives you some certainty plus some flexibility.',
  },
];

export const IMPORTANT_FEATURES = [
  {
    key: 'basic_home_loan',
    label: 'Basic home loan',
    tip: 'A no-frills home loan with fewer features and a lower rate. Good if you want simplicity and the cheapest rate.',
  },
  {
    key: 'offset_account',
    label: 'Offset account',
    tip: 'A transaction account linked to your loan. The balance offsets the loan amount you\'re charged interest on - the more you keep in it, the less interest you pay.',
  },
  {
    key: 'redraw_facility',
    label: 'Redraw facility',
    tip: 'Lets you withdraw extra repayments you\'ve made on the loan. Useful for emergencies or unexpected expenses.',
  },
  {
    key: 'professional_packages',
    label: 'Professional package',
    tip: 'Bundles your home loan, credit card and offset account, often with a discounted rate for an annual fee.',
  },
  {
    key: 'extra_repayments_allowed',
    label: 'Extra repayments allowed',
    tip: 'The ability to make additional repayments on top of your minimum without penalty, paying the loan off faster.',
  },
];

export const FEATURE_ANSWER_OPTIONS = [
  { value: 'Yes', label: 'Yes - I want this' },
  { value: 'NiceToHave', label: 'Nice to have' },
  { value: 'No', label: 'No - not needed' },
];

class StepLoanRequirements extends React.Component {
  patch = (field, value) => {
    this.props.onUpdateLoanRequirements({ [field]: value });
  };

  patchNested = (parent, value) => {
    const cur = this.props.loanRequirements[parent] || {};
    this.patch(parent, { ...cur, ...value });
  };

  patchFeatureAnswer = (key, answer) => {
    const features = { ...(this.props.loanRequirements.important_features || {}) };
    features[key] = { ...(features[key] || {}), answer };
    this.patch('important_features', features);
  };

  render() {
    const lr = this.props.loanRequirements;
    const features = lr.important_features || {};
    const err = this.props.errors || {};

    return (
      <div className="space-y-5">
        {/* Intro */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="Your loan - Requirements"
            title="Loan requirements & preferences"
            description="Help us understand what kind of loan suits you best. Your answers shape which lenders and products we'll match you with."
          />
          <HelperNote>
            <strong>Pick everything that applies.</strong> You can change any of these later when
            chatting with your broker - this just gives us a useful starting point.
          </HelperNote>
        </div>

        <CategoryCard
          icon={ICONS.lightbulb}
          title="Why are you using our service?"
          description="Helps us understand what brought you in."
        >
          <CheckboxGrid
            columns={2}
            options={WHY_OPTIONS}
            value={lr.why}
            onChange={(v) => this.patch('why', v)}
            fieldId="loan_req.why"
            error={err['loan_req.why']}
          />
        </CategoryCard>

        <CategoryCard
          icon={ICONS.target}
          title="What can we assist you with?"
          description="What you're trying to achieve with this loan."
        >
          <CheckboxGrid
            columns={2}
            options={ASSIST_OPTIONS}
            value={lr.what_can_assist}
            onChange={(v) => this.patch('what_can_assist', v)}
            fieldId="loan_req.what_can_assist"
            error={err['loan_req.what_can_assist']}
          />
        </CategoryCard>

        <CategoryCard
          icon={ICONS.trending}
          title="How will you repay the loan?"
          description="The plan to pay it down over time."
        >
          <CheckboxGrid
            columns={2}
            options={PAYMENT_OPTIONS}
            value={lr.payment_plan}
            onChange={(v) => this.patch('payment_plan', v)}
          />
          {lr.payment_plan && lr.payment_plan.other_check ? (
            <div className="mt-3">
              <TextField
                label="Please specify"
                value={(lr.payment_plan || {}).other}
                onChange={(v) => this.patchNested('payment_plan', { other: v })}
              />
            </div>
          ) : null}
        </CategoryCard>

        <CategoryCard
          icon={ICONS.calendar}
          title="Preferred loan term"
          description="Most home loans are 30 years."
        >
          <PillGroup
            columns={4}
            options={TERM_OPTIONS}
            value={lr.loan_term}
            onChange={(v) => this.patch('loan_term', v)}
            fieldId="loan_req.loan_term"
            error={err['loan_req.loan_term']}
          />
          {lr.loan_term === 'other' ? (
            <div className="mt-3">
              <TextField
                label="Term (years)"
                required
                inputMode="numeric"
                value={lr.loan_term_other}
                onChange={(v) => this.patch('loan_term_other', v.replace(/\D/g, ''))}
                fieldId="loan_req.loan_term_other"
                error={err['loan_req.loan_term_other']}
              />
            </div>
          ) : null}
        </CategoryCard>

        <CategoryCard
          icon={ICONS.alert}
          title="Expected financial changes"
          description="Anything coming up that might affect your repayments."
        >
          <PillGroup
            options={['Yes', 'No']}
            value={lr.changes_in_financial_circum}
            onChange={(v) => this.patch('changes_in_financial_circum', v)}
          />
          {lr.changes_in_financial_circum === 'Yes' ? (
            <div className="mt-3">
              <label className="block text-sm font-medium text-[#1F2937] mb-2">
                Tell us more
              </label>
              <textarea
                rows={3}
                value={lr.changes_in_financial_circum_yes || ''}
                onChange={(e) => this.patch('changes_in_financial_circum_yes', e.target.value)}
                placeholder="e.g. one applicant going on parental leave, planned retirement, expected bonus..."
                className="dui-textarea w-full rounded-lg border bg-white p-3 text-sm text-[#1F2937] focus:border-[#7C5CFC] focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
                style={{ borderColor: '#D1D5DB' }}
              />
            </div>
          ) : null}
        </CategoryCard>

        <CategoryCard
          icon={ICONS.refresh}
          title="Repayment type"
          description="How each repayment is structured."
        >
          <div
            data-field-id="loan_req.loan_repayment_type"
            style={
              err['loan_req.loan_repayment_type']
                ? {
                    padding: '6px',
                    margin: '-6px',
                    borderRadius: '14px',
                    boxShadow: '0 0 0 1px #DC2626',
                  }
                : undefined
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {REPAYMENT_OPTIONS.map((opt) => {
                const selected = lr.loan_repayment_type === opt.value;
                return (
                  <div
                    key={opt.value}
                    className="rounded-xl border-2 transition-colors"
                    style={{
                      backgroundColor: selected ? '#EFEAFE' : '#FFFFFF',
                      borderColor: selected ? '#7C5CFC' : '#D1D5DB',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => this.patch('loan_repayment_type', opt.value)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between"
                      style={{ color: selected ? '#5B3FE0' : '#1F2937' }}
                    >
                      <span className="text-sm sm:text-base font-semibold">{opt.label}</span>
                      <Tooltip text={opt.tip} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          {err['loan_req.loan_repayment_type'] ? (
            <p className="mt-2 text-xs font-medium text-[#DC2626]">
              {err['loan_req.loan_repayment_type']}
            </p>
          ) : null}
          {lr.loan_repayment_type === 'Interest Only' ? (
            <div className="mt-3">
              <SelectField
                label="Interest only term"
                required
                placeholder="Select"
                options={INTEREST_ONLY_TERMS}
                value={lr.interest_only_term}
                onChange={(v) => this.patch('interest_only_term', v)}
                fieldId="loan_req.interest_only_term"
                error={err['loan_req.interest_only_term']}
              />
            </div>
          ) : null}
        </CategoryCard>

        <CategoryCard
          icon={ICONS.percent}
          title="Rate type"
          description="Fixed, variable or a mix of both."
        >
          <div
            data-field-id="loan_req.rate_type"
            style={
              err['loan_req.rate_type']
                ? {
                    padding: '6px',
                    margin: '-6px',
                    borderRadius: '14px',
                    boxShadow: '0 0 0 1px #DC2626',
                  }
                : undefined
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {RATE_OPTIONS.map((opt) => {
                const selected = lr.rate_type === opt.value;
                return (
                  <div
                    key={opt.value}
                    className="rounded-xl border-2 transition-colors"
                    style={{
                      backgroundColor: selected ? '#EFEAFE' : '#FFFFFF',
                      borderColor: selected ? '#7C5CFC' : '#D1D5DB',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => this.patch('rate_type', opt.value)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between"
                      style={{ color: selected ? '#5B3FE0' : '#1F2937' }}
                    >
                      <span className="text-sm sm:text-base font-semibold">{opt.label}</span>
                      <Tooltip text={opt.tip} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          {err['loan_req.rate_type'] ? (
            <p className="mt-2 text-xs font-medium text-[#DC2626]">
              {err['loan_req.rate_type']}
            </p>
          ) : null}
        </CategoryCard>

        <CategoryCard
          icon={ICONS.star}
          title="Important features"
          description="For each feature, tell us whether you want it. Tap the info icon if you're not sure what something means."
        >
          <div className="space-y-3">
            {IMPORTANT_FEATURES.map((f) => {
              const fv = features[f.key] || { answer: '' };
              return (
                <div
                  key={f.key}
                  className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-sm font-semibold text-[#1F2937]">{f.label}</p>
                    <Tooltip text={f.tip} />
                  </div>
                  <PillGroup
                    columns={3}
                    options={FEATURE_ANSWER_OPTIONS}
                    value={fv.answer}
                    onChange={(v) => this.patchFeatureAnswer(f.key, v)}
                    fieldId={`loan_req.important_features.${f.key}`}
                    error={err[`loan_req.important_features.${f.key}`]}
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-5">
            <label className="block text-sm font-medium text-[#1F2937] mb-2">
              Anything else you'd like your loan to have?
            </label>
            <textarea
              rows={3}
              value={lr.important_features_other || ''}
              onChange={(e) => this.patch('important_features_other', e.target.value)}
              placeholder="e.g. construction option, parental leave repayment pause, multiple offset accounts..."
              className="dui-textarea w-full rounded-lg border bg-white p-3 text-sm text-[#1F2937] focus:border-[#7C5CFC] focus:outline-none focus:ring-2 focus:ring-[#7C5CFC]"
              style={{ borderColor: '#D1D5DB' }}
            />
          </div>
        </CategoryCard>
      </div>
    );
  }
}

export default StepLoanRequirements;
